import React, { useState } from 'react';
import {
  TextField, Grid, MenuItem, FormControlLabel, Switch,
  Tabs, Tab, Box, Typography
} from '@mui/material';
import { SERVER_TYPES, DIFFICULTIES, GAMEMODES, LEVEL_TYPES } from '../constants';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper to capitalize first letter
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function ServerForm({ data, onChange, isEdit = false }) {
  const [tabValue, setTabValue] = useState(0);
  const [nameError, setNameError] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChange = (field, value) => {
    onChange('server_settings', field, value);
  };

  const handleRootChange = (field, value) => {
    if (field === 'name') {
      // Validate server name
      if (value.length < 2) {
        setNameError('Server name must be at least 2 characters long.');
      } else if (/\s/.test(value)) {
        setNameError('Server name cannot contain spaces.');
      } else if (!/^[a-zA-Z0-9][a-zA-Z0-9_.-]*$/.test(value)) {
        setNameError('Server name can only contain letters, numbers, underscores, periods, and dashes.');
      } else {
        setNameError('');
      }
    }
    onChange('root', field, value);
  };

  const handleSwitchChange = (field, checked) => {
    onChange('server_settings', field, checked ? 'true' : 'false');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="server settings tabs" variant="scrollable" scrollButtons="auto">
          <Tab label="General" />
          <Tab label="World" />
          <Tab label="Players" />
          <Tab label="Networking" />
          <Tab label="Misc" />
          <Tab label="Advanced" />
        </Tabs>
      </Box>

      {/* General Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2}>
          {!isEdit && (
            <Grid item xs={12}>
              <TextField
                label="Server Name"
                fullWidth
                value={data.name || ''}
                onChange={(e) => handleRootChange('name', e.target.value)}
                error={!!nameError}
                helperText={nameError || "Must be at least 2 characters, no spaces. Allowed: a-z, 0-9, _, ., -"}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              select
              label="Server Type"
              fullWidth
              value={data.server_settings.TYPE || 'VANILLA'}
              onChange={(e) => handleChange('TYPE', e.target.value)}
            >
              {SERVER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Minecraft Version"
              fullWidth
              value={data.server_settings.VERSION || ''}
              onChange={(e) => handleChange('VERSION', e.target.value)}
              placeholder="LATEST"
              helperText="You can specify a specific version like 1.21.4 or LATEST for the latest version."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Memory"
              fullWidth
              value={data.server_settings.MEMORY || ''}
              onChange={(e) => handleChange('MEMORY', e.target.value)}
              placeholder="1G"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Max Players"
              fullWidth
              type="number"
              value={data.server_settings.MAX_PLAYERS || ''}
              onChange={(e) => handleChange('MAX_PLAYERS', e.target.value)}
              placeholder="20"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="MOTD"
              fullWidth
              value={data.server_settings.MOTD || ''}
              onChange={(e) => handleChange('MOTD', e.target.value)}
              placeholder="A Minecraft Server"
              helperText="You can use \n for a line break."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Server Icon URL"
              fullWidth
              value={data.server_settings.ICON || ''}
              onChange={(e) => handleChange('ICON', e.target.value)}
              placeholder="http://example.com/icon.png"
              helperText="A URL or file path to a 64x64 PNG image file that will be displayed in the server list."
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={data.server_settings.ONLINE_MODE === 'true'}
                  onChange={(e) => handleSwitchChange('ONLINE_MODE', e.target.checked)}
                />
              }
              label="Online Mode"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Enables the authentication of players with the Mojang servers.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={data.server_settings.USE_AIKAR_FLAGS === 'true'}
                  onChange={(e) => handleSwitchChange('USE_AIKAR_FLAGS', e.target.checked)}
                />
              }
              label="Use Aikar Flags"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              A list of JVM flags to optimize the server performance.
            </Typography>
          </Grid>
        </Grid>
      </TabPanel>

      {/* World Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              select
              label="Difficulty"
              fullWidth
              value={data.server_settings.DIFFICULTY || 'easy'}
              onChange={(e) => handleChange('DIFFICULTY', e.target.value)}
            >
              {DIFFICULTIES.map((diff) => (
                <MenuItem key={diff} value={diff}>{capitalize(diff)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Game Mode"
              fullWidth
              value={data.server_settings.MODE || 'survival'}
              onChange={(e) => handleChange('MODE', e.target.value)}
            >
              {GAMEMODES.map((mode) => (
                <MenuItem key={mode} value={mode}>{capitalize(mode)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Level Name"
              fullWidth
              value={data.server_settings.LEVEL || ''}
              onChange={(e) => handleChange('LEVEL', e.target.value)}
              placeholder="world"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Seed"
              fullWidth
              value={data.server_settings.SEED || ''}
              onChange={(e) => handleChange('SEED', e.target.value)}
              helperText="Leave empty for a random seed."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Level Type"
              fullWidth
              value={data.server_settings.LEVEL_TYPE || 'normal'}
              onChange={(e) => handleChange('LEVEL_TYPE', e.target.value)}
            >
              {LEVEL_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type.split('_').map(capitalize).join(' ')}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="View Distance"
              fullWidth
              type="number"
              value={data.server_settings.VIEW_DISTANCE || ''}
              onChange={(e) => handleChange('VIEW_DISTANCE', e.target.value)}
              placeholder="10"
              helperText="The amount of world data the server sends the player, measured in chunks."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Simulation Distance"
              fullWidth
              type="number"
              value={data.server_settings.SIMULATION_DISTANCE || ''}
              onChange={(e) => handleChange('SIMULATION_DISTANCE', e.target.value)}
              placeholder="10"
              helperText="The maximum distance from players that living entities may be located in order to be updated."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Spawn Protection"
              fullWidth
              type="number"
              value={data.server_settings.SPAWN_PROTECTION || ''}
              onChange={(e) => handleChange('SPAWN_PROTECTION', e.target.value)}
              placeholder="16"
              helperText="The radius in blocks around the world spawn point that is protected from players who are not operators."
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={data.server_settings.HARDCORE === 'true'} onChange={(e) => handleSwitchChange('HARDCORE', e.target.checked)} />}
                label="Hardcore"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                The difficulty is set to hard and the player will be put into spectator mode upon death.
              </Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={data.server_settings.PVP === 'true'} onChange={(e) => handleSwitchChange('PVP', e.target.checked)} />}
              label="PVP"
            />
            <FormControlLabel
              control={<Switch checked={data.server_settings.FORCE_GAMEMODE === 'true'} onChange={(e) => handleSwitchChange('FORCE_GAMEMODE', e.target.checked)} />}
              label="Force Gamemode"
            />
            <FormControlLabel
              control={<Switch checked={data.server_settings.ENABLE_COMMAND_BLOCK === 'true'} onChange={(e) => handleSwitchChange('ENABLE_COMMAND_BLOCK', e.target.checked)} />}
              label="Command Blocks"
            />
            <FormControlLabel
              control={<Switch checked={data.server_settings.ALLOW_NETHER === 'true'} onChange={(e) => handleSwitchChange('ALLOW_NETHER', e.target.checked)} />}
              label="Allow Nether"
            />
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={data.server_settings.GENERATE_STRUCTURES === 'true'} onChange={(e) => handleSwitchChange('GENERATE_STRUCTURES', e.target.checked)} />}
                label="Generate Structures"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Whether the server should generate structures like villages and strongholds.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Players Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Operators (Comma separated)"
              fullWidth
              value={data.server_settings.OPS || ''}
              onChange={(e) => handleChange('OPS', e.target.value)}
              helperText="A list of UUIDs and/or names of players who have the operator role on the server."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Whitelist (Comma separated)"
              fullWidth
              value={data.server_settings.WHITELIST || ''}
              onChange={(e) => handleChange('WHITELIST', e.target.value)}
              helperText="The whitelist ensures that only players who are on the list are allowed to enter the server."
            />
          </Grid>
          <Grid item xs={12}>
             <FormControlLabel
              control={<Switch checked={data.server_settings.ENFORCE_WHITELIST === 'true'} onChange={(e) => handleSwitchChange('ENFORCE_WHITELIST', e.target.checked)} />}
              label="Enforce Whitelist"
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Networking Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Network Compression Threshold"
              fullWidth
              type="number"
              value={data.server_settings.NETWORK_COMPRESSION_THRESHOLD || ''}
              onChange={(e) => handleChange('NETWORK_COMPRESSION_THRESHOLD', e.target.value)}
              placeholder="256"
              helperText="The size of a packet in bytes before it is compressed. -1 disables compression."
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={data.server_settings.PREVENT_PROXY_CONNECTIONS === 'true'} onChange={(e) => handleSwitchChange('PREVENT_PROXY_CONNECTIONS', e.target.checked)} />}
                label="Prevent Proxy Connections"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Prevents players from connecting with an ISP/ASN other than the one they had when authenticating with Mojang.
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={data.server_settings.USE_NATIVE_TRANSPORT === 'true'} onChange={(e) => handleSwitchChange('USE_NATIVE_TRANSPORT', e.target.checked)} />}
                label="Use Native Transport"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Enables optimized packet sending/receiving on Linux.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Misc Tab */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Player Idle Timeout (minutes)"
              fullWidth
              type="number"
              value={data.server_settings.PLAYER_IDLE_TIMEOUT || ''}
              onChange={(e) => handleChange('PLAYER_IDLE_TIMEOUT', e.target.value)}
              placeholder="0"
              helperText="The time in minutes before a player is kicked from the server for being idle."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Resource Pack URL"
              fullWidth
              value={data.server_settings.RESOURCE_PACK || ''}
              onChange={(e) => handleChange('RESOURCE_PACK', e.target.value)}
              helperText="The URL to a ZIP archive containing the resource pack."
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={data.server_settings.ALLOW_FLIGHT === 'true'} onChange={(e) => handleSwitchChange('ALLOW_FLIGHT', e.target.checked)} />}
              label="Allow Flight"
            />
            <FormControlLabel
              control={<Switch checked={data.server_settings.ENABLE_RCON === 'true'} onChange={(e) => handleSwitchChange('ENABLE_RCON', e.target.checked)} />}
              label="Enable RCON"
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Advanced Tab */}
      <TabPanel value={tabValue} index={5}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="JVM Options"
              fullWidth
              value={data.server_settings.JVM_OPTS || ''}
              onChange={(e) => handleChange('JVM_OPTS', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="JVM XX Options"
              fullWidth
              value={data.server_settings.JVM_XX_OPTS || ''}
              onChange={(e) => handleChange('JVM_XX_OPTS', e.target.value)}
            />
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}